import React, { useRef, useState, type ReactNode } from "react";

import clsx from "clsx";
import { isEqual } from "lodash";
import moment from "moment";
import { useEventListener, useOnClickOutside } from "usehooks-ts";

import { type TimeFrame } from "@features/data-export/types";
import {
  flip,
  offset,
  shift,
  useFloating,
  type Placement,
} from "@floating-ui/react";
import {
  isFixedDateInterval,
  todayLocalAsISODate,
  type DateInterval,
  type FixedDateInterval,
  type ICompareDateRangeEnum,
} from "@north-beam/nb-common";
import { DateRangeCalendar } from "@shared/date-range-calendar";
import { Select } from "@shared/selects";
import { type OptionType } from "@shared/selects/select";
import { parseDateRangeArgument } from "@utils/index";

type WithCustomValue<Option extends OptionType> =
  | Option["value"]
  | FixedDateInterval;

const DATE_FORMAT = "MMM DD, YYYY";

interface DateRangePickerProps<Option extends OptionType> {
  label?: string;
  className?: string;
  nonCustomChoices: Option[];
  disabled?: boolean;
  isClearable?: boolean;
  maxLookbackDays?: number;
  icon?: ReactNode;
  dateRange: TimeFrame | ICompareDateRangeEnum | null;
  selectClassName?: string;
  popoverClassName?: string;
  popoverPlacement?: Placement;
  onUpdate: (dateRange: WithCustomValue<Option>) => void;
}

export function DateRangePicker<Option extends OptionType>({
  label,
  dateRange,
  className,
  onUpdate,
  disabled = false,
  isClearable = false,
  nonCustomChoices,
  maxLookbackDays,
  icon,
  selectClassName,
  popoverClassName,
  popoverPlacement,
}: DateRangePickerProps<Option>) {
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const closeCalendar = () => setIsCalendarOpen(false);

  const commit = (newDateRange: WithCustomValue<Option>) => {
    closeCalendar();

    if (!isEqual(newDateRange, dateRange)) {
      onUpdate(newDateRange);
    }
  };

  const firstDropdownChoices: Option[] = [
    ...nonCustomChoices,
    { label: "Custom date...", value: "" } as unknown as Option,
  ];

  const onChange = (value: Option["value"]) => {
    if (value === "") {
      setIsCalendarOpen(true);
    } else {
      commit(value);
    }
  };

  const { refs, floatingStyles } = useFloating({
    open: isCalendarOpen,
    onOpenChange: setIsCalendarOpen,
    transform: false,
    strategy: "fixed",
    placement: popoverPlacement ?? "bottom",
    middleware: [offset(4), flip(), shift()],
  });

  const ref = useRef<HTMLDivElement | null>(null);
  useOnClickOutside(ref, closeCalendar);
  useEventListener("keydown", (event: KeyboardEvent) => {
    if (event.key === "Escape") {
      closeCalendar();
      event.stopPropagation();
    }
  });

  return (
    <div className={clsx(className)} ref={ref}>
      <div className="nb-date-picker">
        {isCalendarOpen && (
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            className={clsx("z-[3001]", popoverClassName)}
          >
            <DateRangeCalendar
              className="bg-white rounded-lg elevation-1"
              maxLookbackDays={maxLookbackDays ?? 20 * 365}
              value={null}
              onApply={commit}
              onCancel={() => setIsCalendarOpen(false)}
            />
          </div>
        )}
      </div>
      <Select
        noPortal
        comboboxRefSetter={refs.setReference}
        label={label}
        value={dateRange}
        isDisabled={disabled}
        isClearable={isClearable}
        formatDisplayValue={() =>
          dateRange ? formatDateRangeText(dateRange) : null
        }
        icon={icon ?? <i className="fa-regular fa-sharp fa-calendar" />}
        onChange={onChange}
        className={selectClassName}
        options={firstDropdownChoices}
      />
    </div>
  );
}

export function formatDateRangeText(or: any) {
  if (isFixedDateInterval(or)) {
    return <FormattedDateRange dateRange={or} />;
  } else {
    return (or as any).label;
  }
}

export function formatDateRange({ startDate, endDate }: DateInterval) {
  const start = moment(startDate);
  const end = moment(endDate);
  if (start.isSame(end, "day")) {
    return start.format(DATE_FORMAT);
  } else if (start.isSame(end, "year")) {
    return `${start.format("MMM D")} – ${end.format("MMM D")}`;
  }

  return `${start.format(DATE_FORMAT)} – ${end.format(DATE_FORMAT)}`;
}

export function FormattedDateRange({ dateRange }: { dateRange: DateInterval }) {
  return <React.Fragment>{formatDateRange(dateRange)}</React.Fragment>;
}

export function DateRangeDisplay({
  dateRange,
  onConversionToFixed,
}: {
  dateRange: TimeFrame;
  onConversionToFixed?: (dr: FixedDateInterval) => void;
}) {
  const today = todayLocalAsISODate();
  const anchor = today;
  const dr = parseDateRangeArgument(dateRange, anchor);
  const isFixed = typeof dateRange !== "string";

  const start = moment(dr.startDate).format(DATE_FORMAT);
  const end = moment(dr.endDate).format(DATE_FORMAT);

  const numDays = moment(dr.endDate).diff(moment(dr.startDate), "days") + 1;
  const noun = numDays === 1 ? "day" : "days";

  let topHalf: React.ReactNode;
  if (isFixed || dateRange === "monthToDate" || dateRange === "yearToDate") {
    topHalf = (
      <div>
        {start} — {end} ({numDays} {noun})
      </div>
    );
  } else {
    const displayable = start === end ? start : `${start} — ${end}`;
    topHalf = (
      <div
        onClick={() =>
          onConversionToFixed && onConversionToFixed({ ...dr, type: "fixed" })
        }
      >
        {displayable}
      </div>
    );
  }

  const showNotice = dr.endDate >= today;
  let bottomHalf: any = null;
  if (showNotice) {
    bottomHalf = (
      <p className="text-danger text-xxs">
        Reports including the current day may return incomplete data.
      </p>
    );
  }

  return (
    <>
      {topHalf}
      {bottomHalf}
    </>
  );
}
